/**
 * Uncontracted (grade 1) Unified English Braille for the Braille Translator.
 *
 * Rule numbers refer to the Rules of Unified English Braille, third edition (2024),
 * published by the International Council on English Braille:
 *  - 6.1-6.3  numeric indicator and numeric mode (a comma or full stop inside a number
 *             keeps numeric mode on; a space, hyphen or any other symbol ends it)
 *  - 6.5.2    a grade 1 indicator is needed when a lowercase letter a-j follows a digit,
 *             full stop or comma in numeric mode
 *  - 8.3      a capital letter is dot 6 plus the lowercase form
 *  - 8.4      the capitals word indicator (two dot-6 cells) for a fully capitalised word
 *  - 8.6      the capitals terminator
 */

export const CAPITAL_INDICATOR = "⠠";
export const NUMBER_INDICATOR = "⠼";
export const GRADE1_INDICATOR = "⠰";
const CAPITALS_TERMINATOR_CELL = "⠄";

export const LETTER_TO_BRAILLE: Record<string, string> = {
  a: "⠁",
  b: "⠃",
  c: "⠉",
  d: "⠙",
  e: "⠑",
  f: "⠋",
  g: "⠛",
  h: "⠓",
  i: "⠊",
  j: "⠚",
  k: "⠅",
  l: "⠇",
  m: "⠍",
  n: "⠝",
  o: "⠕",
  p: "⠏",
  q: "⠟",
  r: "⠗",
  s: "⠎",
  t: "⠞",
  u: "⠥",
  v: "⠧",
  w: "⠺",
  x: "⠭",
  y: "⠽",
  z: "⠵",
};

export const DIGIT_TO_LETTER: Record<string, string> = {
  "1": "a",
  "2": "b",
  "3": "c",
  "4": "d",
  "5": "e",
  "6": "f",
  "7": "g",
  "8": "h",
  "9": "i",
  "0": "j",
};

const LETTER_TO_DIGIT: Record<string, string> = {
  a: "1",
  b: "2",
  c: "3",
  d: "4",
  e: "5",
  f: "6",
  g: "7",
  h: "8",
  i: "9",
  j: "0",
};

const PUNCTUATION_TO_BRAILLE: Record<string, string> = {
  ".": "⠲",
  ",": "⠂",
  ";": "⠆",
  ":": "⠒",
  "!": "⠖",
  "?": "⠦",
  "'": "⠄",
  "-": "⠤",
  "(": "⠐⠣",
  ")": "⠐⠜",
  "/": "⠸⠌",
};

const REVERSE_TOKENS: { braille: string; text: string }[] = [
  ...Object.entries(PUNCTUATION_TO_BRAILLE).map(([text, braille]) => ({
    braille,
    text,
  })),
  ...Object.entries(LETTER_TO_BRAILLE).map(([text, braille]) => ({
    braille,
    text,
  })),
].sort((a, b) => b.braille.length - a.braille.length);

const DIGIT = /^[0-9]$/;
const LOWER_A_TO_J = /^[a-j]$/;
const ASCII_LETTER = /^[A-Za-z]$/;
const WHITESPACE = /^\s$/;

function isDigit(char: string | undefined): boolean {
  return char !== undefined && DIGIT.test(char);
}

function isAsciiLetter(char: string | undefined): boolean {
  return char !== undefined && ASCII_LETTER.test(char);
}

function isUpper(char: string): boolean {
  return isAsciiLetter(char) && char === char.toUpperCase();
}

/** Length of the run of capital letters starting at `index` (letters only). */
function capitalRunEnd(chars: string[], index: number): number {
  let end = index;
  while (end < chars.length && isUpper(chars[end])) {
    end += 1;
  }
  return end;
}

/**
 * A decimal sign with no digit before it (".7", ",7") takes the numeric indicator
 * (rule 6.2); a full stop that follows a letter or word ("No.16") does not.
 */
function startsDecimal(chars: string[], index: number): boolean {
  const char = chars[index];
  if (char !== "." && char !== ",") {
    return false;
  }
  if (!isDigit(chars[index + 1])) {
    return false;
  }
  const before = chars[index - 1];
  return before === undefined || WHITESPACE.test(before);
}

function brailleForLetterRun(chars: string[], start: number, end: number) {
  // A run of two or more capitals is a capitalised word (8.4); the last capital
  // before a lowercase letter belongs to that lowercase letter (TVOntario, McGRAW).
  let runEnd = end;
  if (isAsciiLetter(chars[end]) && runEnd - start > 1) {
    runEnd -= 1;
  }
  let out = "";
  const length = runEnd - start;
  if (length >= 2) {
    out += CAPITAL_INDICATOR + CAPITAL_INDICATOR;
  }
  for (let i = start; i < runEnd; i += 1) {
    if (length < 2) {
      out += CAPITAL_INDICATOR;
    }
    out += LETTER_TO_BRAILLE[chars[i].toLowerCase()];
  }
  return { out, next: runEnd };
}

function encodeOther(char: string, numeric: boolean): string {
  const letterCell = LETTER_TO_BRAILLE[char];
  if (letterCell === undefined) {
    return PUNCTUATION_TO_BRAILLE[char] ?? char;
  }
  // A lowercase letter a-j straight after a digit, full stop or comma needs the
  // grade 1 indicator so it is not read as a digit (6.5.2).
  return numeric && LOWER_A_TO_J.test(char)
    ? GRADE1_INDICATOR + letterCell
    : letterCell;
}

export function textToBraille(text: string): string {
  const chars = [...text];
  let out = "";
  let numeric = false;
  let i = 0;
  while (i < chars.length) {
    const char = chars[i];
    if (isDigit(char)) {
      if (!numeric) {
        out += NUMBER_INDICATOR;
        numeric = true;
      }
      out += LETTER_TO_BRAILLE[DIGIT_TO_LETTER[char]];
      i += 1;
      continue;
    }
    if (startsDecimal(chars, i) && !numeric) {
      out += NUMBER_INDICATOR + PUNCTUATION_TO_BRAILLE[char];
      numeric = true;
      i += 1;
      continue;
    }
    if (numeric && (char === "." || char === ",")) {
      out += PUNCTUATION_TO_BRAILLE[char];
      i += 1;
      continue;
    }
    if (isUpper(char)) {
      const end = capitalRunEnd(chars, i);
      const run = brailleForLetterRun(chars, i, end);
      out += run.out;
      numeric = false;
      i = run.next;
      continue;
    }
    out += encodeOther(char, numeric);
    numeric = false;
    i += 1;
  }
  return out;
}

function matchToken(
  braille: string,
  index: number
): { braille: string; text: string } | null {
  for (const token of REVERSE_TOKENS) {
    if (braille.startsWith(token.braille, index)) {
      return token;
    }
  }
  return null;
}

type Caps = "none" | "next" | "word" | "passage";

interface DecodeState {
  caps: Caps;
  numeric: boolean;
  skipDigit: boolean;
}

function isLetterToken(text: string): boolean {
  return LETTER_TO_BRAILLE[text] !== undefined;
}

/** Counts the run of dot-6 cells at `index`. */
function capitalCellRun(braille: string, index: number): number {
  let count = 0;
  while (braille.charAt(index + count) === CAPITAL_INDICATOR) {
    count += 1;
  }
  return count;
}

function handleCapitalCells(
  braille: string,
  index: number,
  state: DecodeState
): number {
  const run = capitalCellRun(braille, index);
  state.numeric = false;
  // Dot 6 then dot 3 is the capitals terminator (8.6).
  if (run === 1 && braille.charAt(index + 1) === CAPITALS_TERMINATOR_CELL) {
    state.caps = "none";
    return index + 2;
  }
  if (run >= 3) {
    state.caps = "passage";
    return index + 3;
  }
  if (run === 2) {
    state.caps = "word";
    return index + 2;
  }
  // A single capital letter ends capitals word mode but not passage mode (8.4.2).
  state.caps = state.caps === "passage" ? "passage" : "next";
  return index + 1;
}

function resolveToken(token: { text: string }, state: DecodeState): string {
  const { text } = token;
  if (
    state.numeric &&
    !state.skipDigit &&
    LETTER_TO_DIGIT[text] !== undefined
  ) {
    return LETTER_TO_DIGIT[text];
  }
  if (isLetterToken(text) && state.caps !== "none") {
    return text.toUpperCase();
  }
  return text;
}

function afterToken(token: { text: string }, state: DecodeState): void {
  const letter = isLetterToken(token.text);
  const stays =
    state.numeric &&
    !state.skipDigit &&
    (LETTER_TO_DIGIT[token.text] !== undefined ||
      token.text === "." ||
      token.text === ",");
  state.numeric = stays;
  state.skipDigit = false;
  if (state.caps === "next") {
    state.caps = "none";
  } else if (state.caps === "word" && !letter) {
    state.caps = "none";
  }
}

function resetOnUnmatched(char: string, state: DecodeState): void {
  state.numeric = false;
  state.skipDigit = false;
  if (WHITESPACE.test(char) && state.caps !== "passage") {
    state.caps = "none";
  }
}

export function brailleToText(braille: string): string {
  let out = "";
  const state: DecodeState = { caps: "none", numeric: false, skipDigit: false };
  let i = 0;
  while (i < braille.length) {
    const char = braille.charAt(i);
    if (char === NUMBER_INDICATOR) {
      state.numeric = true;
      state.skipDigit = false;
      if (state.caps === "word" || state.caps === "next") {
        state.caps = "none";
      }
      i += 1;
      continue;
    }
    if (char === CAPITAL_INDICATOR) {
      i = handleCapitalCells(braille, i, state);
      continue;
    }
    if (char === GRADE1_INDICATOR) {
      // The grade 1 indicator makes the next cell a letter, not a digit (6.5.2).
      state.skipDigit = true;
      i += 1;
      continue;
    }
    const token = matchToken(braille, i);
    if (token === null) {
      out += char;
      resetOnUnmatched(char, state);
      i += 1;
      continue;
    }
    out += resolveToken(token, state);
    afterToken(token, state);
    i += token.braille.length;
  }
  return out;
}

export const ALPHABET = Object.keys(LETTER_TO_BRAILLE);
export const DIGITS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"];
